import { FunctionComponent, Suspense, useState, useEffect } from 'react';
import { lazy } from 'react';
import { getDataSourceDisplayMemberValue } from './FieldEditor';

// Lazy import the Autocomplete and TextField components from MUI
const MUIAutocomplete = lazy(() => import('@mui/material/Autocomplete'));
const TextField = lazy(() => import('@mui/material/TextField'));

export interface AutoCompleteProps {
    /** The name of the field. */
    field: string;
    /** The label for the editor. */
    label?: string;
    /** Is this field required? */
    isRequired?: boolean;
    /** Is the editing enabled? */
    isEnabled?: boolean;
    /** The placeholder for the field value. */
    placeholder?: string;
    /** The datasource for the autocomplete. If loadOptions is provided, this will be ignored after the initial load. */
    dataSource?: { [index: string]: any }[];
    /** The datasource object's property that is human readable. */
    dataSourceDisplayMember?: string;
    /** The datasource object's property that is used as the real value for the field. */
    dataSourceValueMember?: string;
    /** Is the input invalid? */
    isInvalid?: boolean;
    /** The value of the field. */
    value?: any;
    /** CSS class for the control. */
    className?: string;
    /** Fired when the field value changes in the editor. */
    onValueChanged?: (field: string, value: any) => void;
    /** Async function to load options based on input. 
     * The options need to have the fields for dataSourceValueMember and dataSourceDisplayMember. 
     * No need to set the dataSource property if this is provided. Actually, it will be ignored.
     */
    loadOptions?: (inputValue: string) => Promise<any[] | undefined>;
}

/**
 * Component: a wrapper around the MUI Autocomplete component
 */
const AutoComplete: FunctionComponent<AutoCompleteProps> = (props) => {
    const isEnabled = props.isEnabled ?? true;
    const dataSourceValueMember = props.dataSourceValueMember ?? 'value';
    const dataSourceDisplayMember = props.dataSourceDisplayMember ?? 'display';

    const [options, setOptions] = useState<readonly any[]>(props.dataSource ?? []);
    const [loading, setLoading] = useState(false);
    // Store the selected value object if the input 'value' is just the ID
    const [selectedValueObject, setSelectedValueObject] = useState<any>(null); 
    const [inputValue, setInputValue] = useState(''); // State to control the input value

    // Effect to find the selected object when props.value or props.dataSource changes
    useEffect(() =>
    {
        if (props.value != null && typeof props.value !== 'object' && props.dataSource && props.dataSource.length > 0)
        {
            const found = props.dataSource.find(item => item[dataSourceValueMember] === props.value);
            setSelectedValueObject(found ?? null);
        } 
        else if (props.value != null && typeof props.value === 'object')
        {
            setSelectedValueObject(props.value);
        }
        else
        {
            setSelectedValueObject(null);
        }
    }, [props.value, props.dataSource, dataSourceValueMember]);
    
    // Effect to handle initial options if dataSource is provided
    useEffect(() => {
        setOptions(props.dataSource ?? []);
    }, [props.dataSource]);

    const handleInputChange = async (event: React.SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue); // Update local input value state
        if (props.loadOptions) {
            setLoading(true);
            try {
                const newOptions = await props.loadOptions(newInputValue);
                setOptions(newOptions ?? []);
            } catch (error) {
                console.error('Error loading autocomplete options:', error);
                setOptions([]); // Clear options on error
            } finally {
                setLoading(false);
            }
        }
    };

    // Determine the value prop for MUI Autocomplete based on whether props.value is an object or an ID
    const acValue = typeof props.value === 'object' ? props.value : selectedValueObject;

    /** Render */
    return (
        <Suspense fallback={<div>Loading Autocomplete...</div>}>
            <MUIAutocomplete
                options={options}
                getOptionLabel={(option) => getDataSourceDisplayMemberValue(option, dataSourceDisplayMember)}
                disabled={!isEnabled}
                value={acValue}
                inputValue={inputValue}
                onChange={(event, newValue) => { if (props.onValueChanged) props.onValueChanged(props.field, newValue); }}
                onInputChange={props.loadOptions ? handleInputChange : (event, newInputValue) => setInputValue(newInputValue)}
                loading={loading}
                isOptionEqualToValue={(option: any, value: any) => {
                    // Handle comparison when value might be the full object or just the ID
                    const optionVal = option ? option[dataSourceValueMember] : undefined;
                    const valueVal = value ? (typeof value === 'object' ? value[dataSourceValueMember] : value) : undefined;
                    // Ensure both values are defined before comparing
                    return optionVal !== undefined && valueVal !== undefined && optionVal === valueVal;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        required={props.isRequired}
                        error={props.isInvalid}
                        placeholder={props.placeholder}
                        variant="outlined"
                        className={props.className ?? "field-editor-input"}
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            className: 'form-control'
                        }}
                    />
                )}
            />
        </Suspense>
    );
}

export default AutoComplete;